import { describeError } from "../../../utils/error_message";
import { QUESTION_PLACE } from "../../../constants/question";
import type { QuestionBibleVerse, QuestionPlace } from "../../../types/question.types";

const DRAFT_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

const DRAFT_KEYS = {
	QUESTION: "draftQuestionText",
	MENTOR_REQUEST: "draftMentorRequestText",
};

/** What a draft keeps: the text and the Bible places attached to it. */
export type QuestionDraftRecord = { text: string; verses: QuestionBibleVerse[] };

/** A mentor request and an ordinary question are written in different fields, so they never share. */
export const getDraftKey = (inComponent?: QuestionPlace): string =>
	inComponent === QUESTION_PLACE.REQUEST_MENTOR ? DRAFT_KEYS.MENTOR_REQUEST : DRAFT_KEYS.QUESTION;

/**
 * The draft belongs to the account that typed it, not to the browser.
 *
 * The user id is stored with the text and checked on the way out, so the next person to sign in on
 * a shared computer is not handed someone else's unsent question. A draft written before ids were
 * stored has no owner and is dropped; one written before places were kept reads with none.
 */
export const readDraft = (draftKey: string, userId: string): QuestionDraftRecord | null => {
	let savedDraftData: string | null;

	try {
		savedDraftData = localStorage.getItem(draftKey);
	} catch (error) {
		console.error("Failed to read the question draft", describeError(error));
		return null;
	}

	if (!savedDraftData) return null;

	try {
		const { text, verses, timestamp, userId: draftUserId } = JSON.parse(savedDraftData);

		if (
			draftUserId === userId &&
			Date.now() - timestamp <= DRAFT_EXPIRATION_TIME &&
			typeof text === "string"
		) {
			return { text, verses: Array.isArray(verses) ? verses : [] };
		}
	} catch {
		// A draft that cannot be parsed is a draft that cannot be restored — drop it below.
	}

	removeDraft(draftKey);
	return null;
};

export const writeDraft = (draftKey: string, draft: QuestionDraftRecord, userId: string): void => {
	if (draft.text.trim().length === 0 && draft.verses.length === 0) {
		removeDraft(draftKey);
		return;
	}

	try {
		localStorage.setItem(draftKey, JSON.stringify({ ...draft, timestamp: Date.now(), userId }));
	} catch (error) {
		// A full or blocked storage costs the draft, never the question being written.
		console.error("Failed to store the question draft", describeError(error));
	}
};

/** Places to bring back from the draft — unless the modal was opened with its own, or for a mentor request. */
export const draftVersesToRestore = (
	draft: QuestionDraftRecord | null,
	openedWith: QuestionBibleVerse[],
	isRequestMentor: boolean,
): QuestionBibleVerse[] | null =>
	draft?.verses.length && !openedWith.length && !isRequestMentor ? draft.verses : null;

/**
 * Places worth keeping in the draft. Verses the modal was opened with belong to that one opening:
 * the draft key is shared by every place, so keeping them would attach them to a later course question.
 */
export const draftVersesToSave = (
	verses: QuestionBibleVerse[],
	openedWith: QuestionBibleVerse[],
	isRequestMentor: boolean,
): QuestionBibleVerse[] => (openedWith.length || isRequestMentor ? [] : verses);

export const removeDraft = (draftKey: string): void => {
	try {
		localStorage.removeItem(draftKey);
	} catch (error) {
		console.error("Failed to clear the question draft", describeError(error));
	}
};
